import {
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Form } from 'antd';

import {
  GenericDataFormContext,
  LOADING_SAVED_QUOTATIONS_STATES,
  QUOTATION_BUTTON,
  QuotationButton,
  QuotationInfoContext,
  QuotationStep,
  StepOneFormContext,
} from '../../../../../shared';

import QuotationService, {
  DEFAULT_PAYMENT_PLAN_CODE,
} from '../../../../../../../../../shared/services/quotation';

import { StepThreeFormContext } from './StepThreeFormContext';
import { StepTwoFormContext } from '../../../StepTwo/contexts/StepTwoForm/StepTwoFormContext';

import {
  CivilQuotationRequest,
  CivilQuotationComponent,
  WebCivilQuotation,
} from '../../../../../../../../../shared/interfaces/services/civil-quotation-service';
import {
  GetCommertialsConditionsResponse,
  QuotationCoverage,
} from '../../../../../../../../../shared/interfaces';
import {
  generateInputID,
  getCoverageAndActivityKeyByID,
} from '../../../../../AP/Steps/StepThree/components/Quotation/utils';
import { getValueDefaultPaymentMethod } from '../../../../../utils/get-value-default-payment-method';
import { CivilQuotationFormByTab } from '../../components';
import { WizardDataFormContext } from '../../../../Wizard/contexts/WizardDataForm';
import { LOADING_RENEWAL_QUOTATION_STATES } from '../../../../../shared/interfaces/loading-renewal-quotation-states-enum';
import { LoadQuotationToRenewResponse } from '../../../../../shared/interfaces/load-renewal-quotation-response-interface';
import { scrollToEmitButton } from '../../../../../../../../../shared/utils';

export interface CivilQuotationForm {
  [key: string]: string;
}

export interface CivilPaymentMethodForm {
  [key: string]: string | number;
  paymentMethod: string;
  comission: number;
  extraVariable: number;
  variation: number;
}

export const StepThreeFormProvider: FC<PropsWithChildren> = ({ children }) => {
  const {
    allCoveragesPerPlan,
    allLocalitiesPerProvince,
    paymentMethodsList,
    setCanShowSpinner,
    setPlansSelectedByTabs,
    updateFormsAfterRemoveTabs,
    setTabsUserCanNotDelete,
    removedTabIndexes, 
    setRemovedTabIndexes,
    formValues,
  } = useContext(GenericDataFormContext);
  const {
    currentStep,
    loadSaveQuotationState,
    quotationInfo,
    savedQuotation,
    quotationToRenewal,
    setCurrentStep,
    setLoadSaveQuotationState,
    setRenewalQuotationState,
    renewalQuotationState
  } = useContext(QuotationInfoContext);
  const { clientSelected } = useContext(StepOneFormContext);
  const { handleTabErrors } = useContext(WizardDataFormContext);
  const { tabs, stepTwoForm } = useContext(StepTwoFormContext);

  const [commertialConditions, setCommertialConditions] =
    useState<GetCommertialsConditionsResponse>();
  const [civilQuotationComponents, setCivilQuotationComponents] = useState<
    Array<CivilQuotationComponent>
  >([]);
  const [quotationResponse, setQuotationResponse] =
    useState<WebCivilQuotation>();
  const [labelButton, setLabelButton] = useState<QuotationButton>(
    quotationResponse ? QUOTATION_BUTTON.RECOTIZAR : QUOTATION_BUTTON.COTIZAR
  );
  const [stepThreeCivilQuotationForm] = Form.useForm<CivilQuotationForm>();
  const [civilPaymentMethodsForm] = Form.useForm<CivilPaymentMethodForm>();

  const [quotationTabs, setQuotationTabs] = useState<Array<any>>([]);
  const [activeTab, setActiveTab] = useState(quotationTabs[0]?.key);

  const [haveConsumedService, setHaveConsumedService] =
    useState<boolean>(false);

  const clearPrimaFieldsOnFormChange = useCallback(() => {
    const allFields = stepThreeCivilQuotationForm.getFieldsValue();
    const allPrimaKeys = Object.keys(allFields).filter((el) =>
      el.includes('prima')
    );

    stepThreeCivilQuotationForm.resetFields(allPrimaKeys);
  }, [stepThreeCivilQuotationForm]);

  const getCommercialsConditions = useCallback(async () => {
    try {
      const response = await QuotationService.getComertialsConditions(
        quotationInfo?.branch!
      );

      if (response.data) setCommertialConditions(response.data);
    } catch {}
  }, [quotationInfo, setCommertialConditions]);

  const generateCoveragesInfo = useCallback(
    (tabKey: string): Array<QuotationCoverage> => {
      const quotationCoverages: Array<QuotationCoverage> = [];

      const allFields = stepThreeCivilQuotationForm.getFieldsValue();

      const allCheckboxFields = Object.keys(allFields).filter((el) =>
        el.includes('checkbox')
      );

      const allCheckboxFieldByTab = allCheckboxFields.filter((el) =>
        el.includes(`civil-tab-${tabKey}`)
      );

      allCheckboxFieldByTab.forEach((el) => {
        if (stepThreeCivilQuotationForm.getFieldValue(el)) {
          const { id, activityKey } = getCoverageAndActivityKeyByID(el);

          const planSelected = stepThreeCivilQuotationForm.getFieldValue(
            `plan-tab-${tabKey}`
          );

          const allCoveragesByTab = allCoveragesPerPlan.find(
            (el) => String(el.plan as string) === String(planSelected)
          )?.coverages;

          const coverage = allCoveragesByTab?.find((el) => el.id === id);

          const sumaAseguradaInputName = generateInputID(id, activityKey);

          const primaInputName = generateInputID(id, activityKey, 'prima');

          quotationCoverages.push({
            cobertura: coverage?.codigo!,
            riesgo: coverage?.codigoRiesgo!,
            sumaAsegurada: stepThreeCivilQuotationForm.getFieldValue(
              sumaAseguradaInputName
            ),
            prima:
              stepThreeCivilQuotationForm.getFieldValue(primaInputName) ?? '0',
            pormilaje: '0',
          });
        }
      });

      return quotationCoverages;
    },
    [stepThreeCivilQuotationForm, allCoveragesPerPlan]
  );

  const generateCivilComponentsInfo =
    useCallback((): Array<CivilQuotationComponent> => {
      let components: Array<CivilQuotationComponent> = [];

      tabs.forEach(({ key }: any) => {
        const location: string = stepTwoForm?.getFieldValue(
          `location-tab-${key}`
        );
        const provinceCode: string = stepTwoForm?.getFieldValue(
          `province-tab-${key}`
        );

        const localitiesOfProvince = allLocalitiesPerProvince.find(
          (el) => el.provinceCode === provinceCode
        )?.localities;

        const riskZone =
          localitiesOfProvince?.find(
            (el) => `${el.sufijoCodigoPostal}${el.codigoPostal}` === location
          )?.zonaRiesgosVarios ?? '';

        const component: CivilQuotationComponent = {
          nroComponente: key,
          codPlan: stepTwoForm?.getFieldValue(`plan-tab-${key}`) as number,
          tipoVivienda: 0, //* es 0 para pólizas nuevas.
          caracteristicasBien: [], //* es vacío para Civil
          cantCaracteristicas: 0,
          sufijoCodigoPostal: location[0],
          codigoPostal: location.slice(1),
          zonaRiesgo: riskZone,
          buenResultado: 0, //* es 0 para pólizas nuevas.
          cobertura: generateCoveragesInfo(key),
          psua: 0, //* es 0 para pólizas nuevas.
          capTarifa: '0',
          capTarifaInc1: '',
          capTarifaInc2: '',
          clasificacionRiesgo: 'A',
        };

        components.push(component);
      });

      setCivilQuotationComponents(components);

      return components;
    }, [
      tabs,
      allLocalitiesPerProvince,
      stepTwoForm,
      generateCoveragesInfo,
      setCivilQuotationComponents,
    ]);

  const generateCivilQuotationForm = useCallback((): CivilQuotationRequest => {
    const components = generateCivilComponentsInfo();
    const paymentMethodCode = civilPaymentMethodsForm.getFieldInstance(
      'paymentMethod'
    )
      ? civilPaymentMethodsForm?.getFieldValue('paymentMethod').split('-')[1]
      : getValueDefaultPaymentMethod(paymentMethodsList);
    const administrativeSurcharge = civilPaymentMethodsForm.getFieldInstance(
      'extraVariable'
    )
      ? civilPaymentMethodsForm?.getFieldValue('extraVariable')
      : commertialConditions?.porcentajeExtraPrimaVariable;

    return {
      token: quotationInfo?.token!,
      arse: quotationInfo?.arse!,
      rama: quotationInfo?.branch!,
      codigoTipoFormaPago: paymentMethodCode,
      cantComponentes: components.length,
      cliente: clientSelected!,
      componentes: components,
      recargoAdministrativo: administrativeSurcharge,
    };
  }, [
    quotationInfo,
    clientSelected,
    generateCivilComponentsInfo,
    civilPaymentMethodsForm,
    commertialConditions,
    paymentMethodsList,
  ]);

  const createTabs = useCallback(() => {
    const newTabs = tabs?.map(({ key }: any) => ({
      children: <CivilQuotationFormByTab tabKey={key} />,
      forceRender: true,
      closable: false,
      key,
      label: `Resp. Civil ${key}`,
    }));

    setQuotationTabs(newTabs);

    setPlansSelectedByTabs(tabs, stepTwoForm);
  }, [tabs, setQuotationTabs, stepTwoForm, setPlansSelectedByTabs]);

  const handlerOnClickEmit = useCallback(async () => {
    setCurrentStep(QuotationStep.FOUR);
  }, [setCurrentStep]);

  const updateFieldsBeforeQuotation = useCallback(
    ({ componentes }: WebCivilQuotation) => {
      const paymentMethodCode =
        civilPaymentMethodsForm.getFieldValue('paymentMethod');
      const defaultPaymentMethod = paymentMethodsList?.find(
        (el) => el.codigoFormaDePago === DEFAULT_PAYMENT_PLAN_CODE
      );

      if (
        paymentMethodCode &&
        paymentMethodsList?.find(
          (el) =>
            `${el.codigoFormaDePago}-${el.nroPlanDePago}` === paymentMethodCode
        )
      ) {
        civilPaymentMethodsForm.setFieldValue(
          'paymentMethod',
          paymentMethodCode
        );
      } else {
        civilPaymentMethodsForm.setFieldValue(
          'paymentMethod',
          `${defaultPaymentMethod?.codigoFormaDePago}-${defaultPaymentMethod?.nroPlanDePago}`
        );
      }

      if (
        !civilPaymentMethodsForm.getFieldValue('commission') &&
        !civilPaymentMethodsForm.getFieldValue('extraVariable')
      ) {
        civilPaymentMethodsForm.setFieldValue(
          'commission',
          Number(commertialConditions?.porcentajeDeComision)
        );
        civilPaymentMethodsForm.setFieldValue(
          'extraVariable',
          Number(commertialConditions?.porcentajeExtraPrimaVariable)
        );
      }

      componentes.forEach(({ nroDeComponente, coberturas }) => {
        coberturas.forEach(({ id, prima }) => {
          const primaInputName = generateInputID(
            id!,
            `civil-tab-${nroDeComponente}`,
            'prima'
          );

          stepThreeCivilQuotationForm.setFieldValue(
            primaInputName,
            Number(prima)
          );
        });
      });
    },
    [
      civilPaymentMethodsForm,
      paymentMethodsList,
      commertialConditions,
      stepThreeCivilQuotationForm,
      allCoveragesPerPlan,
    ]
  );

  const handlerOnClickQuotation = useCallback(
    async (form: CivilQuotationRequest) => {
      setCanShowSpinner(true);

      try {
        await stepThreeCivilQuotationForm.validateFields();

        const response = await QuotationService.generateCivilQuotation(form);

        if (response.data) {
          scrollToEmitButton();
          setLabelButton(QUOTATION_BUTTON.EMITIR);

          const tabsUserCanNotDelete: Array<number> = [];

          //recover ID from original coverate and set it into response
          response.data.cotizacionWebResponsabilidadCivil.componentes.forEach(
            ({ nroDeComponente, coberturas }) => {
              const planSelected = stepThreeCivilQuotationForm.getFieldValue(
                `plan-tab-${nroDeComponente}`
              );

              const coveragesPerPlan =
                allCoveragesPerPlan.find(
                  (el) => (el.plan as string) === String(planSelected)
                )?.coverages ?? [];

              coberturas?.forEach((coverage) => {
                const originalCoverage = coveragesPerPlan.find(
                  (item) =>
                    item.codigo === coverage.codigoCobertura &&
                    item.codigoRiesgo === coverage.codigoRiesgo
                );
                coverage.id = originalCoverage?.id;
                coverage.subCategoriaDeTextos = originalCoverage?.subCategoriaDeTextos;
              });

              tabsUserCanNotDelete.push(Number(nroDeComponente));
            }
          );

          setTabsUserCanNotDelete(tabsUserCanNotDelete);
          setQuotationResponse(response.data.cotizacionWebResponsabilidadCivil);
          updateFieldsBeforeQuotation(
            response.data.cotizacionWebResponsabilidadCivil
          );

          return;
        };
      } catch (error: any) {
        if (error && error?.errorFields?.length > 0) {
          handleTabErrors(error?.errorFields, ['tab'], 'Responsabilidad Civil');
        }
      } finally {
        setCanShowSpinner(false);
      }
    },
    [
      stepThreeCivilQuotationForm,
      setLabelButton,
      setCanShowSpinner,
      setQuotationResponse,
      updateFieldsBeforeQuotation,
      setTabsUserCanNotDelete,
    ]
  );

  const handlerOnClick = useCallback(async () => {
    try {
      const form: CivilQuotationRequest = generateCivilQuotationForm();

      if (labelButton === QUOTATION_BUTTON.EMITIR)
        return await handlerOnClickEmit();

      if (labelButton === QUOTATION_BUTTON.COTIZAR || labelButton === QUOTATION_BUTTON.RECOTIZAR)
        return await handlerOnClickQuotation(form);
    } catch (error) {}
  }, [
    labelButton,
    generateCivilQuotationForm,
    handlerOnClickEmit,
    handlerOnClickQuotation,
  ]);

  const fillStepThreeServices = useCallback(async () => {
    try {
      createTabs();
      await getCommercialsConditions();
      setPlansSelectedByTabs(tabs, stepTwoForm);
    } catch (error) {}
  }, [
    getCommercialsConditions,
    setPlansSelectedByTabs,
    tabs,
    stepTwoForm,
    createTabs,
  ]);

  const fillStepThreeData = useCallback(async (isRenewal: boolean) => {
    let quotationToProccess: LoadQuotationToRenewResponse;
      if(isRenewal){
        quotationToProccess = quotationToRenewal;
      }else{
        quotationToProccess = savedQuotation;
      }
    setQuotationResponse({
      sumaAsegurada: '',
      premio: '',
      prima: '',
      componentes: quotationToProccess.bienes.map((bien: any) => {
        return {
          nroDeComponente: bien.numeroComponente,
          producto: '',
          tipoVivienda: '',
          codigoPostal: '',
          sufijoCodigoPostal: '',
          zonaRiesgo: '',
          buenResultado: '',
          sumaAsegurada: '',
          premio: '',
          requiereInspeccion: '',
          porcentajeSumaAsegurada: '',
          capituloTarifa: '',
          capituloTarifaInc1: '',
          agravamiento: '',
          caracteristicas: isRenewal ? [] : savedQuotation.caracteristicasSeleccionadas,
          coberturas: isRenewal ? bien.coberturasSeleccionadas : savedQuotation.coberturasSeleccionadas,
          impuestos: [
            {
              codigoCobertura: '',
              impComision: '',
              impExtraPrimaFija: '',
              impImpuestosInternos: '',
              impIva: '',
              impIvaPercepcion: '',
              impIvaRnl: '',
              impIibbPercepcion: '',
              impIibbRiesgo: '',
              impIibbEmpresa: '',
              porImpuestosInternos: '',
              porIva: '',
              porIvaRnl: '',
              porIvaPercepcion: '',
              porTasaSsn: '',
              porServiciosSociales: '',
              impExtraPrimaVar: '',
              impRecargoFinanciero: '',
              selladoDeEmpresa: '',
              selladoDelRiesgo: '',
              impServiciosSociales: '',
              impTasaSsn: '',
              porComision: '',
              porExtraPrimaVar: '',
              porRecargoFinanciero: '',
              calculoImpuestos: '',
            },
          ],
          bonificaciones: [],
        };
      }),
      impuestos: {
        rama: '1',
        cantPolizaXRama: '',
        prima: '',
        porRecargoFinanciero: '',
        impRecargoFinanciero: '',
        impExtraPrimaFija: '',
        impExtraPrimaVar: '',
        impSubtotalPrima: '',
        selladoDelRiesgo: '',
        selladoDeEmpresa: '',
        porImpuestosInternos: '',
        impImpuestosInternos: '',
        porServiciosSociales: '',
        impServiciosSociales: '',
        porTasaSsn: '',
        impTasaSsn: '',
        impIibbPercepcion: '',
        impIibbEmpresa: '',
        impIibbRiesgo: '',
        impIvaRnl: '',
        porIvaRnl: '',
        impIva: '',
        porIva: '',
      },
      condicionesComerciales: {
        cantPolizaXRama: '',
        porRecargoAdminist: '',
        impRecargoAdminist: '',
        porComisionProd: '',
        impComision: '',
        porRecargoAdminist1: '',
        porComisionProd1: '',
      },
      condicionesComerciales1: {
        rama: '1',
        porcRecargoAdminist: '',
      },
    });

    quotationToProccess.bienes.forEach((bien: any, index: number) => {
      const _tab = index + 1;

      bien.coberturasSeleccionadas.forEach((cobertura: any) => {
        stepThreeCivilQuotationForm.setFields([
          {
            name: `checkbox_${cobertura.codigo}-${cobertura.codigoRiesgo}_civil-tab-${_tab}`,
            value: true,
          },
          {
            name: `prima_${cobertura.codigo}-${cobertura.codigoRiesgo}_civil-tab-${_tab}`,
            value: cobertura.prima,
          },
          {
            name: `sumaAsegurada_${cobertura.codigo}-${cobertura.codigoRiesgo}_civil-tab-${_tab}`,
            value: cobertura.sumaAsegurada,
          },
        ]);
      });
    });

    civilPaymentMethodsForm.setFields([
      {
        name: 'paymentMethod',
        value: `${quotationToProccess.formaDePago.codigo}-${quotationToProccess.formaDePago.codigoDePlanDePagos}`,
      },
      {
        name: 'commission',
        value: quotationToProccess.formaDePago.porcentajeComision,
      },
      {
        name: 'extraVariable',
        value: quotationToProccess.formaDePago.porcentajeExtraPrimaVariable,
      },
      {
        name: 'variation',
        value:
          Number(quotationToProccess.formaDePago.porcentajeComision) -
          Number(commertialConditions!.porcentajeDeComision),
      },
    ]);
    setLabelButton(QUOTATION_BUTTON.RECOTIZAR);
  }, [
    savedQuotation,
    quotationToRenewal,
    commertialConditions,
    setLabelButton,
    setQuotationResponse,
  ]);

  useEffect(() => {
    if (removedTabIndexes.length && currentStep === QuotationStep.THREE && formValues) {
      updateFormsAfterRemoveTabs(removedTabIndexes, [
        stepThreeCivilQuotationForm,
      ], formValues);
      setRemovedTabIndexes([]);
    }
  }, [removedTabIndexes, currentStep, formValues]);

  useEffect(() => {
    if (currentStep === QuotationStep.THREE && !loadSaveQuotationState) {
      getCommercialsConditions();
    }
  }, [currentStep, loadSaveQuotationState, getCommercialsConditions]);

  useEffect(() => {
    if (
      loadSaveQuotationState ===
      LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLED
    ) {
      setLoadSaveQuotationState(
        LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADING
      );
      fillStepThreeServices();
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if (
      loadSaveQuotationState ===
        LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADING &&
      commertialConditions
    ) {
      setLoadSaveQuotationState(
        LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADED
      );
    }
  }, [loadSaveQuotationState, commertialConditions]);

  useEffect(() => {
    if (
      loadSaveQuotationState ===
      LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADED
    ) {
      setLoadSaveQuotationState(
        LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_DATA_FILLING
      );
      fillStepThreeData(false);
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if (
      loadSaveQuotationState ===
        LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_DATA_FILLING &&
      quotationTabs.length === tabs.length &&
      labelButton === QUOTATION_BUTTON.RECOTIZAR &&
      allCoveragesPerPlan.length > 0
    ) {
      setLoadSaveQuotationState(
        LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_DATA_FILLED
      );
    }
  }, [
    loadSaveQuotationState,
    quotationTabs,
    tabs,
    labelButton,
    allCoveragesPerPlan,
  ]);

  useEffect(() => {
    if (
      loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.FINISH_LOADING
    ) {
      handlerOnClick();
    }
  }, [loadSaveQuotationState]);

  /**
   * Renewal
   */

  useEffect(() => {
    if (currentStep === QuotationStep.THREE && !renewalQuotationState) {
      getCommercialsConditions();
    }
  }, [currentStep, renewalQuotationState, getCommercialsConditions]);

  useEffect(() => {
    if (
      renewalQuotationState ===
      LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_DATA_FILLED_RENEWAL
    ) {
      setRenewalQuotationState(
        LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_SERVICES_LOADING_RENEWAL
      );
      fillStepThreeServices();
    }
  }, [renewalQuotationState]);

  useEffect(() => {
    if (
      renewalQuotationState ===
        LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_SERVICES_LOADING_RENEWAL &&
      commertialConditions
    ) {
      setRenewalQuotationState(
        LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_SERVICES_LOADED_RENEWAL
      );
    }
  }, [renewalQuotationState, commertialConditions]);

  useEffect(() => {
    if (
      renewalQuotationState ===
      LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_SERVICES_LOADED_RENEWAL
    ) {
      setRenewalQuotationState(
        LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_DATA_FILLING_RENEWAL
      );
      fillStepThreeData(true);
    }
  }, [renewalQuotationState]);

  useEffect(() => {
    if (
      renewalQuotationState ===
        LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_DATA_FILLING_RENEWAL &&
      quotationTabs.length === tabs.length &&
      labelButton === QUOTATION_BUTTON.RECOTIZAR &&
      allCoveragesPerPlan.length > 0
    ) {
      setRenewalQuotationState(
        LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_DATA_FILLED_RENEWAL
      );
    }
  }, [
    renewalQuotationState,
    quotationTabs,
    tabs,
    labelButton,
    allCoveragesPerPlan,
  ]);

  useEffect(() => {
    if (
      renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.FINISH_LOADING_RENEWAL
    ) {
      handlerOnClick();
    }
  }, [renewalQuotationState]);

  const values = useMemo(
    () => ({
      civilPaymentMethodsForm,
      civilQuotationComponents,
      clearPrimaFieldsOnFormChange,
      commertialConditions,
      generateCivilQuotationForm,
      haveConsumedService,
      labelButton,
      quotationResponse,
      setHaveConsumedService,
      setLabelButton,
      setQuotationResponse,
      stepThreeCivilQuotationForm,
      createTabs,
      quotationTabs,
      setQuotationTabs,
      activeTab,
      setActiveTab,
      handlerOnClick,
    }),
    [
      civilPaymentMethodsForm,
      civilQuotationComponents,
      clearPrimaFieldsOnFormChange,
      commertialConditions,
      generateCivilQuotationForm,
      haveConsumedService,
      labelButton,
      quotationResponse,
      setHaveConsumedService,
      setLabelButton,
      setQuotationResponse,
      stepThreeCivilQuotationForm,
      createTabs,
      quotationTabs,
      setQuotationTabs,
      activeTab,
      setActiveTab,
      handlerOnClick,
    ]
  );

  return (
    <StepThreeFormContext.Provider value={values}>
      {children}
    </StepThreeFormContext.Provider>
  );
};
